import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CalendarClock, Languages, Loader2, Sparkles } from "lucide-react";

const LANGUAGES = [
  { id: "en", label: "English" },
  { id: "nl", label: "Nederlands" },
  { id: "es", label: "Español" },
  { id: "fr", label: "Français" },
  { id: "de", label: "Deutsch" },
  { id: "it", label: "Italiano" },
  { id: "pt", label: "Português" },
];

// Rounded to next hour for default scheduling time
function nextHourISO(offsetHours = 24) {
  const d = new Date();
  d.setHours(d.getHours() + offsetHours, 0, 0, 0);
  return d.toISOString().slice(0, 16); // input[type="datetime-local"]
}

export default function ScheduleDialog({
  open,
  onOpenChange,
  content, // { title, body, hashtags, platform, sourceLanguage, contentType }
  onScheduled,
}) {
  const [mode, setMode] = useState("single"); // single | multi
  const [busy, setBusy] = useState(false);

  // Single-schedule state
  const [scheduledAt, setScheduledAt] = useState(nextHourISO(24));
  const [platform, setPlatform] = useState(content?.platform || "instagram");
  const [language, setLanguage] = useState(content?.sourceLanguage || "en");

  // Multi-schedule state — pick target languages, spread across days
  const [targetLangs, setTargetLangs] = useState(["nl", "en", "fr"]);
  const [startDate, setStartDate] = useState(nextHourISO(24));
  const [hoursApart, setHoursApart] = useState(24);

  if (!content) return null;

  const toggleLang = (id) => {
    setTargetLangs((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  };

  const scheduleSingle = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/content/schedule", {
        scheduled_at: new Date(scheduledAt).toISOString(),
        platform,
        content_type: content.contentType || "social",
        title: content.title || "",
        body: content.body || "",
        hashtags: content.hashtags || [],
        language,
      });
      toast.success("Gepland in kalender");
      onScheduled?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Plannen mislukt");
    } finally { setBusy(false); }
  };

  const scheduleMulti = async () => {
    if (!targetLangs.length) return toast.error("Kies minstens één taal");
    setBusy(true);
    try {
      // Build spread schedules: startDate + n * hoursApart
      const start = new Date(startDate);
      const schedules = targetLangs.map((lang, i) => {
        const t = new Date(start.getTime() + i * hoursApart * 3600 * 1000);
        return { language: lang, scheduled_at: t.toISOString() };
      });
      const { data } = await api.post("/content/multi-schedule", {
        platform,
        content_type: content.contentType || "social",
        title: content.title || "",
        body: content.body || "",
        hashtags: content.hashtags || [],
        source_language: content.sourceLanguage || "en",
        schedules,
      });
      const nonSource = targetLangs.filter((l) => l !== (content.sourceLanguage || "en")).length;
      toast.success(
        `${data.created_count} items ingepland · ${nonSource > 0 ? `${nonSource * 2} credits gebruikt voor vertalingen` : "geen credits gebruikt"}`
      );
      onScheduled?.(data.items);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Multi-plannen mislukt");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121214] border-white/10 max-w-lg" data-testid="schedule-dialog">
        <DialogHeader>
          <div className="font-mono-tech text-[10px] text-amber-400 mb-1">PLAN · IN · KALENDER</div>
          <DialogTitle className="font-heading text-2xl tracking-tight">
            Wanneer wil je dit posten?
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            Voeg dit toe aan je content-kalender. Je kunt het later kopiëren en publiceren, of automatisch laten vertalen naar meerdere talen.
          </DialogDescription>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex bg-[#0b0b0d] border border-white/5 rounded-md p-1 gap-1">
          <button
            onClick={() => setMode("single")}
            data-testid="schedule-mode-single"
            className={`flex-1 px-3 py-2 text-xs rounded font-medium transition-colors flex items-center justify-center gap-1.5 ${
              mode === "single" ? "bg-amber-500 text-black" : "text-zinc-400 hover:text-white"
            }`}
          >
            <CalendarClock className="w-3.5 h-3.5" strokeWidth={1.5} /> Één datum
          </button>
          <button
            onClick={() => setMode("multi")}
            data-testid="schedule-mode-multi"
            className={`flex-1 px-3 py-2 text-xs rounded font-medium transition-colors flex items-center justify-center gap-1.5 ${
              mode === "multi" ? "bg-amber-500 text-black" : "text-zinc-400 hover:text-white"
            }`}
          >
            <Languages className="w-3.5 h-3.5" strokeWidth={1.5} /> Multi-taal
          </button>
        </div>

        {mode === "single" ? (
          <div className="space-y-3">
            <div>
              <Label className="font-mono-tech text-[10px] text-zinc-500">DATUM · TIJD</Label>
              <Input
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                data-testid="schedule-datetime"
                className="mt-2 bg-[#0b0b0d] border-white/10 h-11"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-mono-tech text-[10px] text-zinc-500">PLATFORM</Label>
                <Select value={platform} onValueChange={setPlatform}>
                  <SelectTrigger data-testid="schedule-platform" className="mt-2 bg-[#0b0b0d] border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1e] border-white/10">
                    <SelectItem value="instagram">Instagram</SelectItem>
                    <SelectItem value="tiktok">TikTok</SelectItem>
                    <SelectItem value="youtube">YouTube</SelectItem>
                    <SelectItem value="facebook">Facebook</SelectItem>
                    <SelectItem value="linkedin">LinkedIn</SelectItem>
                    <SelectItem value="twitter">X / Twitter</SelectItem>
                    <SelectItem value="blog">Blog</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-mono-tech text-[10px] text-zinc-500">TAAL</Label>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger data-testid="schedule-language" className="mt-2 bg-[#0b0b0d] border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1e] border-white/10">
                    {LANGUAGES.map((l) => (
                      <SelectItem key={l.id} value={l.id}>{l.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label className="font-mono-tech text-[10px] text-zinc-500 mb-2 block">
                DOELTALEN · WORDEN · AUTOMATISCH · VERTAALD
              </Label>
              <div className="grid grid-cols-4 gap-2">
                {LANGUAGES.map((l) => {
                  const active = targetLangs.includes(l.id);
                  return (
                    <button
                      key={l.id}
                      onClick={() => toggleLang(l.id)}
                      data-testid={`schedule-lang-${l.id}`}
                      className={`text-xs rounded-md border px-2 py-2 transition-colors ${
                        active
                          ? "border-amber-500 bg-amber-500/10 text-amber-300"
                          : "border-white/10 bg-[#0b0b0d] text-zinc-400 hover:border-white/25"
                      }`}
                    >
                      {l.id.toUpperCase()}
                    </button>
                  );
                })}
              </div>
              <div className="mt-2 text-[10px] text-zinc-500 flex items-center gap-1.5">
                <Sparkles className="w-3 h-3 text-amber-400" strokeWidth={1.5} />
                Bron: {(content.sourceLanguage || "en").toUpperCase()} · niet-bron talen kosten 2 credits per vertaling
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-mono-tech text-[10px] text-zinc-500">START · DATUM</Label>
                <Input
                  type="datetime-local"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  data-testid="schedule-start"
                  className="mt-2 bg-[#0b0b0d] border-white/10 h-11"
                />
              </div>
              <div>
                <Label className="font-mono-tech text-[10px] text-zinc-500">TUSSEN · POSTS</Label>
                <Select value={String(hoursApart)} onValueChange={(v) => setHoursApart(Number(v))}>
                  <SelectTrigger data-testid="schedule-spread" className="mt-2 bg-[#0b0b0d] border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1e] border-white/10">
                    <SelectItem value="1">1 uur</SelectItem>
                    <SelectItem value="4">4 uur</SelectItem>
                    <SelectItem value="12">12 uur</SelectItem>
                    <SelectItem value="24">1 dag</SelectItem>
                    <SelectItem value="48">2 dagen</SelectItem>
                    <SelectItem value="168">1 week</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="font-mono-tech text-[10px] text-zinc-500">PLATFORM</Label>
              <Select value={platform} onValueChange={setPlatform}>
                <SelectTrigger data-testid="schedule-multi-platform" className="mt-2 bg-[#0b0b0d] border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1e] border-white/10">
                  <SelectItem value="instagram">Instagram</SelectItem>
                  <SelectItem value="tiktok">TikTok</SelectItem>
                  <SelectItem value="youtube">YouTube</SelectItem>
                  <SelectItem value="facebook">Facebook</SelectItem>
                  <SelectItem value="linkedin">LinkedIn</SelectItem>
                  <SelectItem value="twitter">X / Twitter</SelectItem>
                  <SelectItem value="blog">Blog</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            className="bg-transparent border-white/15 hover:bg-white/5"
          >
            Annuleren
          </Button>
          <Button
            onClick={mode === "single" ? scheduleSingle : scheduleMulti}
            disabled={busy}
            data-testid="schedule-submit"
            className="bg-amber-500 hover:bg-amber-600 text-black font-semibold"
          >
            {busy ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Bezig…</>
            ) : mode === "single" ? (
              "Plan in kalender"
            ) : (
              `Vertaal & plan ${targetLangs.length} items`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
